import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { HeartPulse, Lock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

// Returns { granted, loading, grant } for the current user's health-data consent.
export function useHealthConsent() {
  const [granted, setGranted] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(() => {
    setLoading(true);
    api.get("/consent/me")
      .then((r) => {
        const rows = r.data?.consents || [];
        const hd = rows.find((c) => c.consent_type === "health_data");
        setGranted(!!(hd && hd.granted));
      })
      .catch(() => setGranted(false))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const grant = useCallback(async () => {
    await api.post("/consent", { consent_type: "health_data", level: "granted" });
    setGranted(true);
  }, []);

  return { granted, loading, grant, refresh };
}

export default function HealthDataConsent({ open, onOpenChange, onGranted }) {
  const [busy, setBusy] = useState(false);

  const accept = async () => {
    setBusy(true);
    try {
      await api.post("/consent", { consent_type: "health_data", level: "granted" });
      onGranted?.();
      onOpenChange?.(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="health-consent-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#0D5C75]">
            <HeartPulse size={20} /> Health Data Consent
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm text-[#475467] leading-relaxed">
          <DialogDescription className="sr-only">Consent to processing of your sensitive health data.</DialogDescription>
          <p>
            To match you with the right accredited providers and coordinate your treatment, SarvaSeva needs your
            explicit consent to process sensitive health information (such as your condition, medical history and
            preferences).
          </p>
          <p className="flex items-start gap-2 rounded-xl bg-[#E9F5F9] p-3 text-[#0D5C75]">
            <Lock size={16} className="mt-0.5 shrink-0" />
            <span>Your health data is encrypted (AES-256) and shared only with the provider you choose. You can withdraw consent and request deletion at any time.</span>
          </p>
          <p className="text-xs text-muted-foreground">
            See our <Link to="/privacy" className="text-[#0D5C75] underline">Privacy Policy</Link> and{" "}
            <Link to="/terms" className="text-[#0D5C75] underline">Terms of Service</Link>.
          </p>
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => onOpenChange?.(false)} data-testid="health-consent-decline-btn"
            className="rounded-full border-[#E4E7EC]">Not now</Button>
          <Button onClick={accept} disabled={busy} data-testid="health-consent-accept-btn"
            className="rounded-full bg-[#0D5C75] text-white hover:bg-[#094A5E] hover:text-white">
            {busy ? "Saving…" : "I consent"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
